(ns common.schema.field-spec
  "Shared field spec (client + server): identity, semantics, UI hints, and validation."
  (:require [common.schema.i18n :as i18n]))

(def SystemValueControl
  [:enum
   ;; The persisted value is fully owned by backend/business logic. Users and
   ;; tenant admins may observe it only through authorization and visibility.
   :field.value-control/system-only
   ;; Backend/business logic supplies a default when the caller leaves the value
   ;; empty, but the caller may provide an explicit value when authorized.
   :field.value-control/system-prefilled
   ;; Normal user/application data. System code may still validate or transform
   ;; the value, but it is not the source of truth for the value.
   :field.value-control/user-provided])

(def SystemNullability
  [:enum
   ;; A nil/missing value breaks system/business invariants.
   :field.nullability/system-required
   ;; Nil is structurally allowed. Tenant/pipeline configuration may still make
   ;; the field mandatory in a particular workflow.
   :field.nullability/optional])

(def SystemClearability
  [:enum
   ;; The value may be cleared when the actor has write permission.
   :field.clearability/clearable
   ;; The value may be absent initially, but once set must not be cleared.
   :field.clearability/not-clearable-after-set])

(def SystemModifiability
  [:enum
   ;; Only system code may write after creation.
   :field.modifiability/system-only
   ;; Non-system actors may provide an initial value, but not modify it later.
   :field.modifiability/create-only
   ;; Non-system actors may modify the value when authorized.
   :field.modifiability/user-editable])

(def FieldSystemSemantics
  [:map {:closed true}
   [:field.system/value-control {:optional true} SystemValueControl]
   [:field.system/nullability {:optional true} SystemNullability]
   [:field.system/clearability {:optional true} SystemClearability]
   [:field.system/modifiability {:optional true} SystemModifiability]
   ;; Optional long-term hook for values derived from backend/business context,
   ;; such as defaulting an opportunity owner to the current account.
   ;; Let's leave this out from the plans for now
   ;[:field.system/default-fn {:optional true} fn?]
   ])

(def TenantAdminConfigurationScope
  [:enum
   :field.admin-config/mandatoriness
   :field.admin-config/formatting
   :field.admin-config/constraints])

(def TenantAdminConfiguration
  [:map {:closed true}
   ;; What tenant admins may configure for this field. Detailed setting payloads
   ;; still live in existing tenant/pipeline configuration documents; this target
   ;; model only answers whether this field allows that configuration category.
   ;; Empty/missing means the field is not tenant-admin configurable.
   [:field.admin-config/scopes {:optional true} [:set TenantAdminConfigurationScope]]
   ;; Use when scopes are intentionally blocked by system semantics, e.g. fully
   ;; system-controlled values.
   [:field.admin-config/blocked-reason {:optional true} keyword?]])

(def tenant-admin-configuration-scopes
  #{:field.admin-config/mandatoriness
    :field.admin-config/formatting
    :field.admin-config/constraints})

(defn system-required-field?
  [field-spec]
  (or (true? (:field/system-required? field-spec))
      (true? (:field/system-mandatory field-spec))
      (= :field.nullability/system-required
         (get-in field-spec [:field/system-semantics :field.system/nullability]))))

(defn legacy-always-mandatory-field?
  ([field-spec]
   (legacy-always-mandatory-field? field-spec nil))
  ([field-spec context]
   (let [mandatory (:field/mandatory field-spec)]
     (or (true? mandatory)
         (= :mandatory/always mandatory)
         (and (some? context)
              (fn? mandatory)
              (mandatory context))))))

(defn legacy-positive-mandatory-field?
  [field-spec]
  (let [mandatory (:field/mandatory field-spec)]
    (or (true? mandatory)
        (contains? #{:mandatory/always :mandatory/partially}
                   mandatory))))

(defn legacy-partially-mandatory-field?
  [field-spec]
  (= :mandatory/partially (:field/mandatory field-spec)))

(defn system-required-or-legacy-always-mandatory-field?
  [field-spec]
  (or (system-required-field? field-spec)
      (legacy-always-mandatory-field? field-spec)))

(defn system-required-or-legacy-positive-mandatory-field?
  [field-spec]
  (or (system-required-field? field-spec)
      (legacy-positive-mandatory-field? field-spec)))

(defn system-prefilled-field?
  [field-spec]
  (= :field.value-control/system-prefilled
     (get-in field-spec [:field/system-semantics :field.system/value-control])))

(defn system-only-field?
  [field-spec]
  (or (= :field.value-control/system-only
         (get-in field-spec [:field/system-semantics :field.system/value-control]))
      (true? (:field/system-controlled? field-spec))))

(defn not-clearable-after-set-field?
  [field-spec]
  (or (true? (:field/not-clearable? field-spec))
      (= :field.clearability/not-clearable-after-set
         (get-in field-spec [:field/system-semantics :field.system/clearability]))))

(defn live-update-paths
  "The live-update paths of a field. A field without the key, and an attribute
  that has no field spec, follow every reference."
  [field-spec]
  (get field-spec :field/live-update-paths [:live-paths/all]))

(defn follows-live-scope?
  "True when the live scope follows the reference that the field holds."
  [field-spec]
  (not= [] (live-update-paths field-spec)))

(defn tenant-admin-configuration-blocked?
  [field-spec]
  (some? (get-in field-spec [:field/tenant-admin-configuration :field.admin-config/blocked-reason])))

(defn tenant-admin-configurable-scopes
  [field-spec]
  (if (tenant-admin-configuration-blocked? field-spec)
    #{}
    (set (get-in field-spec [:field/tenant-admin-configuration :field.admin-config/scopes]))))

(defn tenant-admin-configurable-for?
  [field-spec scope]
  (contains? (tenant-admin-configurable-scopes field-spec)
             scope))

(defn tenant-admin-mandatoriness-configurable?
  [field-spec]
  (tenant-admin-configurable-for? field-spec :field.admin-config/mandatoriness))

(defn tenant-admin-formatting-configurable?
  [field-spec]
  (tenant-admin-configurable-for? field-spec :field.admin-config/formatting))

(defn tenant-admin-constraints-configurable?
  [field-spec]
  (tenant-admin-configurable-for? field-spec :field.admin-config/constraints))

(defn no-history?
  "True when the field spec asks for :db/noHistory on its Datomic attribute."
  [field-spec]
  (true? (:field/no-history? field-spec)))

(def LiveUpdatePaths
  "The paths along which a change reaches the live scope of an entity. A field
  without the key has [:live-paths/all]: the live scope follows the reference.
  An empty vector blocks the reference: the live scope does not follow it in
  either direction, and a change of its value is not a change of the scope.
  Explicit paths do not exist yet. The schema refuses them, because a path that
  the live scope ignores would look like a path that works."
  [:or
   [:= []]
   [:= [:live-paths/all]]])

(def CommonFieldSpec
  [:map {:closed true}
   ;; Identity
   [:field/attribute keyword?]                              ;; e.g. :company/name
   [:field/data-type keyword?]                              ;; e.g. :data.type/text-simple ;; validate data type with data type malli / enum

   ;; vector of entity type keywords - cannot use enum? since the list is growing, can we validate at least :entity.type/string?
   ;; entity-type malli validations - enum of all entity types
   [:field/entity-types {:optional true} vector?]
   ;; Display (minimal; full i18n lives elsewhere)
   ;; Custom fields use this as label
   [:field/name {:optional true} string?]                   ;; human label (fallback if i18n missing) ->> what to do with duplicates? all are unique or combine names with entity-types / field configuration paths

   ;; reporting db name
   ;;[:field/machine-column-name string?]                     ;; f.e. reporting database column name - must not be any of the SQL (all dialects) reserved words


   ;; Target model for system-level behavior. This separates facts that were
   ;; historically blended into visibility, authorization, mandatory config,
   ;; and not-clearable flags:
   ;; - system-only values, such as :common/updated-on timestamps
   ;; - values that must structurally exist, such as :entity/type
   ;; - values prefilled by system logic, such as :oppo/owner
   ;; - values that may be initially empty but cannot be cleared after set
   [:field/system-semantics {:optional true} FieldSystemSemantics]

   ;; Which references the live-update feeds follow. Absent means all of them.
   [:field/live-update-paths {:optional true} LiveUpdatePaths]
   ;; Backwards-compatible bridge keys. Prefer :field/system-semantics for
   ;; new schema work, but these are accepted while existing helpers migrate.
   [:field/system-required? {:optional true} boolean?]
   [:field/system-mandatory {:optional true} [:maybe boolean?]]

   ;; Target model for tenant-admin configurable behavior. System-controlled
   ;; fields should generally expose no scopes here; user-provided fields can
   ;; opt in to mandatoriness, formatting, and constraint controls.
   [:field/tenant-admin-configuration {:optional true} TenantAdminConfiguration]

   ;; the mandatoriness is controlled by the TA on custom entity creation configs - deprecated ?
   [:field/mandatory {:optional true} [:enum :mandatory/always :mandatory/partially]]

   ;; not fully enforced
   ;; enforced only during the import validation -> re-think if this is really needed in field definitions at all

   [:field/not-clearable? {:optional true} boolean?]
   ;; do we need this anymore after the custom creation configs are ready ?
   [:field/required-at-creation? {:optional true} boolean?]

   ;; Datomic :db/noHistory. When true, the index keeps only the current value
   ;; of the attribute. Old values are not in d/history and not in d/as-of
   ;; reads. The transaction log keeps them. Setting the flag later does not
   ;; remove the history that exists already: excise it separately.
   ;; backend.db.migrate reads this key to build the schema datoms.
   [:field/no-history? {:optional true} boolean?]

;  :pipeline/fund -> default fund that should be added to any entity in the pipeline i.e.
  ; :opp/fund is prefilled with (:pipeline/fund pipeline) -> if this is added in front end user can change it, but if user does not have permissions to :oppo/fund the creation fails

;:common/tenant -> only written by system - cannot edited - cannot be changed - does it need to be in client at all ?

;; Visibility & UI hints (kept lean; FE owns components)
   [:ui/default-editor-type keyword?]                       ;; e.g. :editor.type/text
   [:ui/allowed-editor-types [:set keyword?]]
   [:ui/default-filter-type keyword?]
   [:ui/allowed-filter-types [:set keyword?]]

   ;; Value validation (shared)
   [:validation-spec {:optional true} any?]                 ;; Malli schema for the value

;; Add the deprecated fields as optional
   [:field/translations {:optional true} i18n/Translations-en-open]
   [:field/default-visibility {:optional true} map?]
   [:field/default-authorization {:optional true} map?]     ;; TODO: fix malli model to allow read/create/write/list choices only -- split write into write-at-creation and write-anytime
   [:field/constraints {:optional true} map?]

   [:field/system-controlled? {:optional true} boolean?]    ;; in-use-disabled?
   [:field/tenant-specific? {:optional true} boolean?]      ;; Tenant configuration
   [:field/active-by-default? {:optional true} boolean?]    ;; default visibility -> new fields or new tenant without configs
   ])

(def DeprecatedFieldSpec
  [:map
   [:field/translations i18n/Translations-en-open]
   [:field/default-visibility {:optional true} map?]
   [:field/constraints {:optional true} map?]               ;; rethink and use as configuration on Tenant level instead, the system level constraints should be handled by the validations-spec - and config is tenant specific
   [:field/for-entity-types {:optional true} set?]          ;; TODO: rethink, used by custom field to define to which entity types the new field can be used with - maybe renate custom-field-entity-type(s) ?

   [:field/name-attribute {:optional true} keyword?]        ;; used by old grid to know which column is name field
   [:field/lower-case-str? {:optional true} boolean?]])     ;; used for duplicate resolution - can we deal with datomic funcitons instead
